import { Fragment, useState } from 'react';

import Button from 'components/Button';
import Icon, { type IconName } from 'components/Icon';
import IconText from 'components/IconText';
import Rating from 'components/Rating';
import Text from 'components/Text';
import type { FieldKey } from 'hooks';
import { useLocaleFormatter, useRelativeTimeFormatter } from 'hooks/format';
import type { Review as ReviewModel, ReviewImage } from 'models/api';
import type { MarketCode } from 'models/sitecore';
import type { TemplateString } from 'types';
import { formatTemplate } from 'utils/format';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { ENTITIES, upperFirst } from 'utils/string';

import ReviewFeedback from './ReviewFeedback';
import ReviewImageThumbnails from './ReviewImageThumbnails';

const MARKET_FLAGS: Record<Lowercase<MarketCode>, IconName> = {
	at: 'location-austria',
	de: 'location-germany',
	fi: 'location-finland',
	no: 'location-norway',
	pl: 'location-poland',
	se: 'location-sweden',
};

export interface ReviewImageClickArgs {
	image: ReviewImage;
	review: ReviewModel;
}

interface BaseProps {
	className?: string;
	feedbackDoneText: string;
	flagAltText: TemplateString | FieldKey;
	marketLang?: string;
	productNotRecommendedText: TemplateString | FieldKey;
	productRecommendedText: TemplateString | FieldKey;
	reportButtonText: string;
	review: ReviewModel;
	reviewImageButtonLabel: string;
	showOriginalTextButtonText: string;
	showTranslationToLanguageButtonText: TemplateString | FieldKey;
	translatedFromLanguageText: TemplateString | FieldKey;
	upvoteButtonText: string;
	verifiedBuyerLabel: string;
}
interface PropsWithImages extends BaseProps {
	onReviewImageClick: (args: ReviewImageClickArgs) => void;
	showImages?: true;
}
interface PropsWithoutImages extends BaseProps {
	onReviewImageClick?: never;
	showImages: false;
}
type Props = PropsWithImages | PropsWithoutImages;

/** A single product review. */
export default function Review({
	className,
	feedbackDoneText,
	flagAltText,
	onReviewImageClick,
	productNotRecommendedText,
	productRecommendedText,
	reportButtonText,
	review,
	reviewImageButtonLabel,
	showImages = true,
	showOriginalTextButtonText,
	showTranslationToLanguageButtonText,
	translatedFromLanguageText,
	upvoteButtonText,
	verifiedBuyerLabel,
}: Props) {
	const {
		author,
		cons,
		date,
		extract,
		feedbackUrl,
		images,
		lang,
		market,
		original,
		pros,
		questions,
		recommended,
		responses,
		score,
		verifiedBuyer,
		votesUp,
	} = review;
	const { t, tPlural } = useI18n();
	const formatRelativeTime = useRelativeTimeFormatter();
	const formatLanguage = useLocaleFormatter('language');
	const formatRegion = useLocaleFormatter('region');

	const [showOriginal, setShowOriginal] = useState(false);
	const handleTranslationToggleClick = () => {
		setShowOriginal((value) => !value);
	};

	// Also need current lang for the switch button.
	const hasOriginalLang =
		lang &&
		original?.lang &&
		(original.extract || original.pros || original.cons);
	let mainText = extract;
	let prosText = pros;
	let consText = cons;
	if (showOriginal && hasOriginalLang) {
		mainText = original.extract;
		prosText = original.pros;
		consText = original.cons;
	}

	const starCountText = tPlural(
		'product_review_stars_screen_reader_text',
		score,
		{
			starCount: score,
		},
	);

	return (
		<article className={className} aria-label={`${author}, ${starCountText}`}>
			<div className="flex items-center gap-x-2">
				{market && (
					<Icon
						name={MARKET_FLAGS[market.toLowerCase()]}
						role="img"
						aria-hidden={false}
						aria-label={formatTemplate(flagAltText, {
							country: formatRegion(market),
						})}
					/>
				)}
				<Text as="p" className="font-bold">
					{author}
				</Text>
			</div>
			<div className="mt-2 flex items-start text-sm">
				<Text as="pSmall" text={upperFirst(formatRelativeTime(date))} />
				{verifiedBuyer && (
					<>
						<span>&nbsp;-&nbsp;</span>
						<IconText
							size="sm"
							iconColor="successActive"
							icon="circle-check-outlined"
							text={verifiedBuyerLabel}
							as="span"
						/>
					</>
				)}
			</div>
			<Text as="pSmall" className="mt-2">
				{hasOriginalLang && (
					<>
						{!showOriginal &&
							formatTemplate(translatedFromLanguageText, {
								language: formatLanguage(original.lang),
							})}{' '}
						<Button
							onClick={handleTranslationToggleClick}
							variant="text-inherit"
							hasExpandedHitArea
						>
							{showOriginal
								? formatTemplate(showTranslationToLanguageButtonText, {
										language: formatLanguage(lang),
									})
								: showOriginalTextButtonText}
						</Button>
					</>
				)}
			</Text>

			<Rating
				score={score}
				className="mt-4"
				reviewCountVariant="hidden"
				displayMode="block"
			/>

			{mainText && <p className="mt-4">{mainText}</p>}

			{(prosText || consText) && (
				<div className="mt-4 flex flex-col gap-2">
					{prosText && (
						<div className="flex">
							<Icon
								name="plus"
								role="img"
								aria-label="+"
								aria-hidden={false}
								className="-mt-[0.125em] mr-2 shrink-0"
							/>
							<Text as="p">{prosText}</Text>
						</div>
					)}
					{consText && (
						<div className="flex">
							<Icon
								name="minus"
								role="img"
								aria-label={ENTITIES.minus}
								aria-hidden={false}
								className="-mt-[0.125em] mr-2 shrink-0"
							/>
							<Text as="p">{consText}</Text>
						</div>
					)}
				</div>
			)}

			{showImages && onReviewImageClick && (
				<ReviewImageThumbnails
					buttonLabel={reviewImageButtonLabel}
					className="mt-6"
					images={images?.map((image) => ({ ...image, review }))}
					onImageClick={onReviewImageClick}
				/>
			)}

			{is.arrayWithLength(questions) && (
				<div className="mt-6 space-y-1">
					{questions.map((question) => (
						<p key={question.name}>
							{t(`review_questions_${question.name}_heading`)}
							{': '}
							{question.options
								.filter((option) => option.selected)
								.map((option, index, array) => (
									<Fragment key={option.value}>
										{t(
											`review_questions_${question.name}_${option.value}_text`,
										)}
										{index < array.length - 1 ? ', ' : '.'}
									</Fragment>
								))}
						</p>
					))}
				</div>
			)}

			{is.defined(recommended) && (
				<Text className="mt-1" as="p">
					{formatTemplate(
						recommended ? productRecommendedText : productNotRecommendedText,
						{ name: author },
					)}
				</Text>
			)}

			{is.arrayWithLength(responses) && (
				<div className="mt-6 rounded-md bg-subtle p-4 forced-colors:border">
					<div className="flex flex-col gap-2">
						{responses.map((response) => (
							<Fragment key={`${response.date}-${response.reply}`}>
								<div className="flex items-center">
									<Icon name="j" className="mr-2" />
									<Text as="p" className="font-bold" text={response.name} />
								</div>
								<Text as="pSmall" className="ml-8">
									{response.reply}
								</Text>
							</Fragment>
						))}
					</div>
				</div>
			)}

			<ReviewFeedback
				className="mt-6"
				feedbackDoneText={feedbackDoneText}
				feedbackUrl={feedbackUrl}
				reportButtonText={reportButtonText}
				upvoteButtonText={upvoteButtonText}
				votesUp={votesUp}
			/>
		</article>
	);
}
Review.displayName = 'Review';
